/**
 * Single source of truth for where a user lands after authentication.
 *
 * Decision table (531-1558 / Onboarding: Solo design §4.1), in the order the
 * implementation below evaluates it:
 *   1. b2b, no org                             -> '/org/new' (create step; idempotent for
 *      abandoned flows — this rule is NOT conditioned on isNewSignup, so a
 *      returning b2b user without an org comes back here too)
 *   2. new signup, b2c, no org                 -> '/onboarding/trial?from=signup' (531-1557: the
 *      card-less trial interstitial; the marker drives the middleware session-build
 *      retry covering the user.created webhook race)
 *   3. new signup, no/unknown segment, no org  -> '/get-started?from=signup' (segment chooser)
 *   4. not onboarded at the current version    -> '/onboarding' — ANY workspace. Org members
 *      included: subscription enforcement still bounces an unsubscribed org to
 *      /organization/billing before the wizard renders, and the router does
 *      NOT duplicate that rule.
 *   5. else                                    -> '/'
 *
 * Membership guards rules 1-3 only. An invited user arrives with a membership
 * AND ?from=signup (Clerk auto-accepts the __clerk_ticket before our router
 * runs), and must never see the chooser, the org-create step or the card-less
 * trial interstitial. They fall through to rule 4 with the marker kept, so the
 * retry budget survives the first wizard request. Rule 4 READS
 * hasCompletedOnboarding on that path — callers must pass the settled value,
 * not a placeholder (see /auth-success).
 *
 * Workspace landings go to '/' (531-1640). The root IS the coach dashboard and
 * nothing there auto-opens a chat, so landing on it is safe. '/dashboard' still
 * renders the same surface, but only as the Coach world's explicit address — the
 * world toggle and the other "go to Coach" controls target it. Landings do not:
 * a later ticket (531-1643) makes '/' resolve the user's default world, and every
 * landing that already targets '/' picks that up for free.
 */
export interface PostAuthRouteInput {
	/** True when the user just completed sign-up (carried via ?from=signup). */
	isNewSignup: boolean;
	/** publicMetadata.segment ?? unsafeMetadata.segment ?? null */
	segment: 'b2c' | 'b2b' | (string & {}) | null;
	/** True when the user has at least one Clerk org membership. */
	hasOrgMembership: boolean;
	/** Kept for callers; the onboarding gate no longer reads it (531-1558). */
	isPersonalWorkspace: boolean;
	/** completedAt set AND onboardingVersion >= ONBOARDING_VERSION — computed by useOnboarding. */
	hasCompletedOnboarding: boolean;
}

export function resolvePostAuthRoute(input: PostAuthRouteInput): string {
	if (!input.hasOrgMembership) {
		if (input.segment === 'b2b') {
			return '/org/new';
		}

		if (input.isNewSignup && input.segment === 'b2c') {
			// 531-1557: our card-less trial interstitial, not Clerk's paywall. The marker keeps
			// the middleware retry budget alive for the first request after signup.
			return '/onboarding/trial?from=signup';
		}

		if (input.isNewSignup) {
			// New signup that has not yet picked a segment (incl. OAuth signups) lands
			// on the dedicated chooser. /get-started writes the segment, then routes to
			// the personal or org-creation branch. (531 team-signup refactor.)
			//
			// 531-1516: ?from=signup is REQUIRED, not cosmetic. Every new-signup landing
			// reaches the app while the user.created webhook may still be in flight, and
			// proxy.ts isSignupContext grants the ~9.5s session-build retry budget (plus
			// the softer "One moment…" 503 copy) only on that marker or a known
			// post-signup pathname. Keep every isNewSignup branch marked.
			return '/get-started?from=signup';
		}
	}

	if (!input.hasCompletedOnboarding) {
		return input.isNewSignup ? '/onboarding?from=signup' : '/onboarding';
	}

	return '/';
}

/**
 * Where a user lands after PAYING — rules 4/5 of the table above, and nothing else.
 *
 * 531-1574. The post-pay settle used to call `resolvePostAuthRoute` with two literals whose only
 * job was to stop rules 1–3 from firing: `segment: null` held rule 1 shut, and
 * `hasOrgMembership: !isPersonalWorkspace` was a WORKSPACE read standing in for a MEMBERSHIP read
 * (a personal workspace can belong to someone with org memberships — 531-1573's whole population).
 * The two mis-stated the user in ways that happened to cancel; changing either alone sent a
 * b2b-segment personal user to `/org/new` immediately after they had paid for a personal plan, and
 * the settle tests pin only the DESTINATION, so they would not have noticed.
 *
 * The fix is structural rather than a better comment: a post-PAY destination has no business
 * passing through a post-AUTH resolver's rules 1–3, so it does not. `/org/new` is unreachable from
 * here as a TYPE-LEVEL fact — there is no `segment` field on the input to set — which is a stronger
 * guarantee than any assertion about its value could be.
 *
 * `?from=signup` is deliberately absent. Rule 4 adds it for a new signup; paying is not signing up,
 * and the marker exists to buy the proxy a session-build retry over the `user.created` webhook
 * race, which is long settled by the time anyone reaches a checkout.
 */
export function resolvePostPayRoute(input: { hasCompletedOnboarding: boolean }): string {
	return input.hasCompletedOnboarding ? '/' : '/onboarding';
}

/**
 * The query key `/auth-success` reads for the post-sign-in deep link (531-1695).
 *
 * Both doors pass `forceRedirectUrl='/auth-success?next=<path>'` to Clerk, which beats the
 * `redirect_url` search param the proxy writes on the unauthenticated bounce — that param is
 * otherwise consumed by clerk-js and lands the user on the deep link WITHOUT this router
 * (the page header of src/app/auth-success/page.tsx has the history). The doors read
 * `redirect_url`, pass it through `safeNextPath`, and hand the survivor to this key.
 */
export const NEXT_QUERY_KEY = 'next';

/** Landing on any of these from `next` is a loop, not a destination. Segment-gated below. */
const NEXT_PATH_DENYLIST = ['/auth-success', '/login', '/register'];

/**
 * A `next` value the router may follow, or null. Root-relative paths only.
 *
 * Two guards, both needed. The parse against a fixed origin drops anything that resolves to another
 * origin (`https://evil.com/x`, `//evil.com`, `javascript:`). The check on the SERIALISED output
 * catches what the parser creates while normalising: dot segments collapse, so `/.//evil.com` comes
 * out as the pathname `//evil.com`, which a browser follows to evil.com. The leading-slash and
 * backslash checks on the raw input close the same class from the other side — browsers treat `\`
 * as `/` in special-scheme URLs, so `/\evil.com` is `//evil.com` by the time it is followed. A
 * rejected value degrades to the workspace landing, which is where every sign-in landed before
 * 531-1695.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
	if (typeof raw !== 'string' || raw.length === 0) return null;
	if (!raw.startsWith('/') || raw.startsWith('//')) return null;
	if (/[\\\u0000- \u007f]/.test(raw)) return null;
	let url: URL;
	try {
		url = new URL(raw, 'http://local');
	} catch {
		return null;
	}
	if (url.origin !== 'http://local') return null;
	// The denylist is compared on the DECODED pathname: `/%6Cogin` is `/login` to the router.
	// A malformed escape throws, and that is a rejection too.
	let decodedPathname: string;
	try {
		decodedPathname = decodeURIComponent(url.pathname);
	} catch {
		return null;
	}
	if (NEXT_PATH_DENYLIST.some((route) => decodedPathname === route || decodedPathname.startsWith(route + '/'))) {
		return null;
	}
	// The guard is on the SERIALISED output, not the input: WHATWG parsing collapses dot segments, so
	// `/.//evil.com`, `/a/..//evil.com` and `/%2e//evil.com` all come out as the pathname `//evil.com`,
	// which a browser follows to evil.com. (Found in task review, 2026-10-08.)
	const path = url.pathname + url.search + url.hash;
	return path.startsWith('//') ? null : path;
}
